import { allMainColors, PolymorphicChart, ScoreCard, ScoreCardProps } from "@foodpilot/foods";
import type { ChartType } from "@foodpilot/foods";
import { Box } from "@mui/material";
import { ScoreWithVariation } from "src/services/ScoreService";
import i18n from "src/i18n";
import { formatNumber } from "src/utils/formatting";
import { isAllowedChartType } from "src/utils/chart";
import { t } from "i18next";
import SpecificityRateDisplay from "src/components/Subscore/SpecificityRate/SpecificityRateDisplay.tsx";
import { NumericalScoreValue } from "src/api/client-api/olapScores/olapScoresSchema";
import { isAppInPDFMode } from "src/utils/rendering";

export type ScoreChartValues = {
    values: number[];
    labels: string[];
    colors?: string[];
};

export type ScoreChartProps = {
    appearance?: "legacy" | "product-v2";
    data?: ScoreChartValues;
    totalScore?: number | null;
    scoresWithVariations: ScoreWithVariation[];
    selectedVariation: ScoreWithVariation;
    isLoading?: boolean;
    disableVariations?: boolean;
    withLegend?: boolean;
    maxScore?: number[];
    specificityRate?: NumericalScoreValue | undefined;
};

export const ScoreChart = (props: ScoreChartProps) => {
    const {
        appearance = "legacy",
        data,
        totalScore,
        specificityRate,
        scoresWithVariations,
        selectedVariation,
        isLoading,
        disableVariations = false,
        withLegend = true,
        maxScore,
    } = props;

    const validPrecision = Math.min(Math.max(selectedVariation?.precision, 0), 100) || 0;
    const isPdfMode = isAppInPDFMode();

    const variationOptions = scoresWithVariations.map((variation) => ({
        ...variation,
        onClick: () => {
            variation.onClick();
        },
        disabled: disableVariations !== false && variation.variationId !== null,
    }));

    const scoreCardHeader: ScoreCardProps["header"] = {
        selectedItemId: selectedVariation?.rowId,
        options: variationOptions.map((d) => ({ ...d, id: d.rowId })),
        variant: "secondary",
    };

    const buildChartValues = () => {
        if (!data) return { values: [], labels: [], colors: undefined };

        const validScoreEntries = data.values.flatMap((value, index) => {
            if (isNaN(value)) return [];

            const roundingThreshold = 0.5 * Math.pow(10, validPrecision);
            const roundedScoreEntry = Number(value.toFixed(validPrecision));

            return [
                {
                    value: value < roundingThreshold ? value : roundedScoreEntry,
                    label: data.labels[index],
                    color: data.colors?.[index],
                },
            ];
        });

        const values = validScoreEntries.map((scoreElement) => scoreElement.value);
        const labels = validScoreEntries.map((scoreElement) => scoreElement.label);
        const colors = data.colors ? validScoreEntries.map((scoreElement) => scoreElement.color as string) : undefined;

        return { values, labels, colors };
    };

    const { values, labels, colors } = buildChartValues();
    const chartColors = colors ?? Object.values(allMainColors).slice(0, values.length);

    const datavizType = selectedVariation?.datavizType ?? undefined;
    const chartTypeValue: ChartType = datavizType && isAllowedChartType(datavizType) ? datavizType : "donut";

    const scoreCard = (
        <ScoreCard header={scoreCardHeader} disableFooter>
            <>
                <PolymorphicChart
                    chartType={chartTypeValue}
                    values={values}
                    labels={labels}
                    colors={chartColors}
                    createPalette={colors ? false : undefined}
                    isLoading={isLoading}
                    displayTotal={`${formatNumber(Number(totalScore), i18n.resolvedLanguage, { maximumFractionDigits: validPrecision, minimumFractionDigits: validPrecision })}`}
                    valuesUnit={selectedVariation?.unit}
                    legend={withLegend ? { position: "bottom" } : undefined}
                    valuesFormatterFn={(value: number) =>
                        formatNumber(value, i18n.resolvedLanguage, {
                            maximumFractionDigits: validPrecision,
                            minimumFractionDigits: validPrecision,
                        })
                    }
                    spacing={2}
                    hoverOffset={0}
                    borderWidth={2}
                    maxScore={chartTypeValue === "raw" ? maxScore : undefined}
                    aggregation={{
                        showAggregateToggle: !isPdfMode && values.length > 1,
                        aggregateToggleLabel: t(
                            "components.chart.aggregate_toggle_label",
                            "Group scores with less than 1%",
                        ),
                        othersCategoryLabel: t("components.chart.others_category_label", "Others"),
                        othersThresholdPercent: 1,
                    }}
                />
                <SpecificityRateDisplay
                    appearance={appearance}
                    specificityRate={specificityRate}
                    isExtendedInfo={true}
                />
            </>
        </ScoreCard>
    );

    if (appearance === "legacy") {
        return scoreCard;
    }

    return (
        <Box
            sx={{
                display: "contents",
                "& > .MuiBox-root": { height: "auto" },
            }}
        >
            {scoreCard}
        </Box>
    );
};
